/* =====================================================================
   SidekickMobile, Mobile Enhancements
   ---------------------------------------------------------------------
   Three small handlers, all gated by window.matchMedia('(max-width: 767px)')
   so they never run (and never inject anything) on desktop / tablet.

   1. Mobile tel CTA , inject a small phone-icon button into the header.
   2. Sticky bottom CTA, clone the primary page CTA into a fixed bar on
      product / order-in pages, with the current price visible.
   3. Filter drawer   , collapse the aside filter sidebar on refurb list
      pages into a bottom-sheet triggered by a "Filters" button.

   Loaded via {{ 'mobile-enhancements.js' | asset_url | script_tag }} in
   layout/theme.liquid, deferred so it never blocks render.
   ===================================================================== */
(function () {
  'use strict';

  // Single source of truth for the mobile breakpoint. Matches the
  // `@media (max-width: 767px)` block in mobile-optimisations.css.
  var MOBILE_MQ = window.matchMedia('(max-width: 767px)');

  function isMobile() {
    return MOBILE_MQ.matches;
  }

  function once(fn) {
    var called = false;
    return function () {
      if (called) return;
      called = true;
      return fn.apply(this, arguments);
    };
  }

  /* ===================================================================
     1) Mobile tel CTA, inject a phone button in the header.
     =================================================================== */
  function injectMobileTelCta() {
    if (!isMobile()) return;
    // Already injected? Skip.
    if (document.querySelector('.skm-tel-cta-mobile')) return;

    // Find the header's right-side button cluster (contains the cart button).
    var cartBtn = document.getElementById('CartTrigger');
    if (!cartBtn || !cartBtn.parentElement) return;

    var tel = document.createElement('a');
    tel.href = 'tel:0490187440';
    tel.className = 'skm-tel-cta-mobile';
    tel.setAttribute('aria-label', 'Call SidekickMobile on 0490 187 440');
    tel.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">'
      + '<path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.24 1.02l-2.21 2.2z"/>'
      + '</svg>';

    // Insert before the cart button so order is: tel | cart | hamburger
    cartBtn.parentElement.insertBefore(tel, cartBtn);
  }

  /* ===================================================================
     2) Sticky bottom CTA, runs on product / order-in / stock-product pages.
     Strategy: find the primary CTA (Enquire/Add to cart) + the price element
     and mirror them into a fixed bar. Click on the bar's button triggers a
     synthetic click on the underlying CTA so all existing event handlers
     (forms, modals, route handlers) keep firing as-is.
     =================================================================== */
  function injectStickyCta() {
    if (!isMobile()) return;
    if (document.getElementById('SkmStickyCta')) return; // already injected

    // Order-In product pages already show their own large "Enquire Now"
    // button inline with the Colour/Storage/Grade selectors. The duplicate
    // sticky bar (a) added visual noise the user complained about and (b)
    // suffered from a textContent bug that leaked the icon name
    // "event_available" into the visible label. Skip injection on those
    // pages, detected by the presence of #EnquireBtn or #OrderInPage.
    if (document.getElementById('EnquireBtn')) return;
    if (document.getElementById('OrderInPage')) return;

    // Identify a primary CTA on the current page. Order of preference:
    //   - product-form Add to cart button (refurb product page)
    //   - .skm-primary-cta  (escape hatch we can opt sections in with)
    var atcBtn = document.querySelector('form[action*="/cart/add"] button[type="submit"], button[name="add"]');
    var optInBtn = document.querySelector('.skm-primary-cta');
    var srcBtn = atcBtn || optInBtn;
    if (!srcBtn) return;
    var enquireBtn = null;

    // Try to find a price element to show on the left side of the bar.
    var priceEl = document.getElementById('ModelPrice')
      || document.querySelector('[data-product-price]')
      || document.querySelector('.product-price')
      || document.querySelector('.price-item--regular')
      || document.querySelector('.price-item--sale');

    // Title for the price-line label.
    var titleEl = document.getElementById('ModelTitle')
      || document.querySelector('main h1');

    // Build the sticky bar.
    var bar = document.createElement('div');
    bar.id = 'SkmStickyCta';
    bar.setAttribute('aria-label', 'Quick action bar');

    var meta = document.createElement('div');
    meta.className = 'skm-sticky-meta';
    var label = document.createElement('div');
    label.className = 'skm-sticky-label';
    label.textContent = enquireBtn ? 'Estimated' : 'Your selection';
    var price = document.createElement('div');
    price.className = 'skm-sticky-price';
    price.textContent = priceEl ? priceEl.textContent.trim() : (titleEl ? titleEl.textContent.trim() : '');
    meta.appendChild(label);
    meta.appendChild(price);

    var action = document.createElement('button');
    action.type = 'button';
    action.className = 'skm-sticky-action';
    var srcLabel = (srcBtn.textContent || '').trim();
    // Compact label: keep first 3 words max to avoid overflow on tiny phones.
    if (srcLabel.split(/\s+/).length > 3) {
      srcLabel = srcLabel.split(/\s+/).slice(0, 3).join(' ');
    }
    if (!srcLabel) {
      srcLabel = enquireBtn ? 'Enquire' : 'Add to cart';
    }
    var icon = document.createElement('span');
    icon.className = 'material-symbols-outlined';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = enquireBtn ? 'event_available' : 'shopping_cart';
    action.appendChild(icon);
    action.appendChild(document.createTextNode(srcLabel));

    action.addEventListener('click', function (e) {
      e.preventDefault();
      // Bring the source CTA into view for context, then synthesise a click.
      try {
        srcBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) { /* old browser, no smooth */ }
      // Give scroll a beat before triggering the click so form errors render
      // near the actual button rather than off-screen.
      setTimeout(function () { srcBtn.click(); }, 220);
    });

    bar.appendChild(meta);
    bar.appendChild(action);

    document.body.appendChild(bar);
    document.body.classList.add('skm-has-sticky-cta');

    // Keep the sticky price in sync if the source price element changes
    // (eg. order-in page recalculates price on storage/grade change).
    if (priceEl) {
      var obs = new MutationObserver(function () {
        price.textContent = priceEl.textContent.trim();
      });
      obs.observe(priceEl, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ===================================================================
     3) Filter drawer, convert the aside filter on refurb listings into a
     bottom-sheet on mobile. Pure UX wrapper; we don't touch the filter
     logic itself, just toggle visibility via a body class.
     =================================================================== */
  function setupFilterDrawer() {
    if (!isMobile()) return;
    // Already wrapped? Skip.
    if (document.getElementById('SkmFiltersBackdrop')) return;

    // The brand sections (samsung/google/oppo/iphone-refurbs.liquid) now
    // ship their own per-brand drawer with a `Mobile<Brand>FiltersToggle`
    // trigger and use `skm-<brand>-filters-open` body classes. The legacy
    // setup below opens via a generic `skm-filters-open` class AND has an
    // aside-level `change` listener that calls closeDrawer() the moment
    // any checkbox flips, which on real phones was auto-closing the new
    // brand drawer as soon as the user tapped a SERIES chip. Bail out
    // when the new system is present so the two don't tug at each other.
    if (document.querySelector('button[id^="Mobile"][id$="FiltersToggle"]')) return;

    var aside = document.querySelector(
      'aside#SamsungFilters, aside#OppoFilters, aside#GoogleFilters, aside#IPhoneFilters'
    );
    if (!aside) return;

    // Backdrop sibling that closes the drawer when tapped.
    var backdrop = document.createElement('div');
    backdrop.id = 'SkmFiltersBackdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(backdrop);

    // Inject the close-row at the top of the aside so users can dismiss
    // the sheet from inside it.
    if (!aside.querySelector('.skm-filters-close-row')) {
      var closeRow = document.createElement('div');
      closeRow.className = 'skm-filters-close-row';
      var title = document.createElement('span');
      title.className = 'skm-filters-title';
      title.textContent = 'Filters';
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'skm-filters-close-btn';
      closeBtn.setAttribute('aria-label', 'Close filters');
      closeBtn.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">close</span>';
      closeBtn.addEventListener('click', closeDrawer);
      closeRow.appendChild(title);
      closeRow.appendChild(closeBtn);
      aside.insertBefore(closeRow, aside.firstChild);
    }

    // Inject the "Filters" trigger button at the top of the grid column.
    var resultCount = document.getElementById('SamsungResultCount')
      || document.getElementById('OppoResultCount')
      || document.getElementById('GoogleResultCount')
      || document.getElementById('IPhoneResultCount');

    if (resultCount && resultCount.parentElement) {
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'skm-filters-trigger';
      trigger.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">tune</span>Filters';
      trigger.addEventListener('click', openDrawer);
      resultCount.parentElement.insertBefore(trigger, resultCount.nextSibling);
    }

    backdrop.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('skm-filters-open')) {
        closeDrawer();
      }
    });

    function openDrawer() {
      document.body.classList.add('skm-filters-open');
    }
    function closeDrawer() {
      document.body.classList.remove('skm-filters-open');
    }

    // Auto-close the drawer when the user picks a filter so they can see
    // the result. Listen on aside via event delegation.
    aside.addEventListener('change', function (e) {
      if (e.target.matches('input[type="checkbox"], input[type="radio"], select')) {
        // Tiny delay so the user sees the selection animate before close.
        setTimeout(closeDrawer, 220);
      }
    });
    // Same for the "Clear" button.
    aside.addEventListener('click', function (e) {
      if (e.target.closest('[id^="Clear"]') ||
          e.target.matches('.Samsung-size-filter, .Oppo-size-filter, .Google-size-filter, .IPhone-size-filter')) {
        setTimeout(closeDrawer, 220);
      }
    });
  }

  /* ===================================================================
     4) Reviews carousel, split each 3-up page into 3 individual pages
     on mobile so the carousel cycles through ALL reviews one-at-a-time
     instead of stacking 3 vertically per page. Driven entirely by DOM
     restructure, the existing carousel JS picks up the new page count
     automatically because it uses `querySelectorAll('.reviews-page')`.
     =================================================================== */
  function restructureReviewsCarousel() {
    if (!isMobile()) return;
    var carousel = document.getElementById('ReviewsCarousel');
    if (!carousel) return;
    if (carousel.classList.contains('skm-mobile-restructured')) return;

    var track = carousel.querySelector('.reviews-track');
    if (!track) return;
    var existingPages = Array.from(track.querySelectorAll('.reviews-page'));
    if (existingPages.length === 0) return;

    // Collect every review card across every page, in order.
    var cards = [];
    existingPages.forEach(function (page) {
      Array.from(page.children).forEach(function (card) {
        cards.push(card);
      });
    });
    if (cards.length === 0) return;

    // The original inline carousel script attaches autoplay + drag handlers
    // to the original `track` and `dots` nodes. We need to wipe those so
    // they don't fight with our mobile cycle. Cloning the carousel element
    // removes all event listeners while preserving the DOM, then we
    // reinsert it.
    var freshCarousel = carousel.cloneNode(false); // empty clone
    // Build the new track inside the fresh carousel.
    var newTrack = document.createElement('div');
    newTrack.className = 'reviews-track flex transition-transform duration-700 ease-out';
    newTrack.style.willChange = 'transform';
    cards.forEach(function (card) {
      var page = document.createElement('div');
      page.className = 'reviews-page flex-shrink-0 w-full';
      page.appendChild(card);
      newTrack.appendChild(page);
    });
    freshCarousel.appendChild(newTrack);
    freshCarousel.classList.add('skm-mobile-restructured');
    carousel.parentNode.replaceChild(freshCarousel, carousel);

    // Now run our own clean cycle on the fresh track.
    var page = 0;
    var totalPages = cards.length;
    var AUTO_DELAY = 4000;
    var timer = null;
    function go(i) {
      page = (i + totalPages) % totalPages;
      newTrack.style.transform = 'translateX(-' + (page * 100) + '%)';
    }
    function start() {
      if (timer) return;
      timer = setInterval(function () { go(page + 1); }, AUTO_DELAY);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    go(0);
    start();

    // Touch swipe support, drag the track horizontally and snap to the
    // nearest page on release.
    var startX = null;
    var dx = 0;
    var dragging = false;
    var THRESH = 50;
    newTrack.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      dx = 0;
      dragging = true;
      newTrack.style.transition = 'none';
      stop();
    }, { passive: true });
    newTrack.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      dx = e.touches[0].clientX - startX;
      var w = newTrack.offsetWidth;
      var off = (page * 100) - (dx / w * 100);
      newTrack.style.transform = 'translateX(-' + off + '%)';
    }, { passive: true });
    newTrack.addEventListener('touchend', function () {
      if (!dragging) return;
      dragging = false;
      newTrack.style.transition = '';
      if (Math.abs(dx) > THRESH) {
        go(page + (dx < 0 ? 1 : -1));
      } else {
        go(page);
      }
      startX = null;
      dx = 0;
      setTimeout(start, 6000);
    });
  }

  /* ===================================================================
     5) Move the rating stars from beside "600+ 5 Star Reviews" up above
     the "What Our Customers Say" heading. Desktop keeps the original
     layout (stars and text on one line beneath the heading).
     =================================================================== */
  function reorderReviewsHeader() {
    if (!isMobile()) return;
    var h2s = document.querySelectorAll('main h2');
    for (var i = 0; i < h2s.length; i++) {
      if (!/Customers Say/i.test(h2s[i].textContent)) continue;
      var h2 = h2s[i];
      var parent = h2.parentElement;
      if (!parent) return;
      var ratingRow = parent.querySelector('.flex.items-center.gap-2');
      if (!ratingRow) return;
      var stars = ratingRow.querySelector('.flex.text-\\[\\#FFD700\\]')
        || ratingRow.querySelector('div.flex');
      if (!stars) return;
      if (stars.dataset.skmReordered) return;
      stars.dataset.skmReordered = '1';
      // Promote the stars to a sibling above the H2.
      parent.insertBefore(stars, h2);
      return;
    }
  }

  /* ===================================================================
     6) Trim "Open Monday to Friday." from the Visit Our Store paragraph
     on mobile only. The paragraph keeps the lead-in copy intact.
     =================================================================== */
  function trimStoreParagraph() {
    if (!isMobile()) return;
    var h2s = document.querySelectorAll('main h2');
    for (var i = 0; i < h2s.length; i++) {
      if (!/Visit\s+Our\s+Store/i.test(h2s[i].textContent)) continue;
      var p = h2s[i].parentElement
        ? h2s[i].parentElement.querySelector('p.text-slate-600, p.text-base, p')
        : null;
      if (!p) return;
      var original = p.textContent || '';
      if (!/Open\s+Monday\s+to\s+Friday/i.test(original)) return;
      p.textContent = original.replace(/\s*Open\s+Monday\s+to\s+Friday\.?\s*$/i, '').trim();
      return;
    }
  }

  /* (Afterpay badge injector removed, we now display the inline afterpay-pill
     row inside the card body instead of overlaying a badge on the image.) */

  /* (Visit Us directions chip removed per user feedback, just the address
     is shown now.) */

  /* ===================================================================
     10) Brand picker, replace the "Other Brands" tile on mobile with
     a subtle "Don't see your brand? Click here" text link below the
     grid. Tapping the link triggers a real click on the original
     (CSS-hidden) Other Brands button so the page's own drill-down
     handler opens the drop-down panel, NOT navigating away.
     =================================================================== */
  function injectOtherBrandsLink() {
    if (!isMobile()) return;
    var grid = document.getElementById('brand-selection');
    if (!grid) return;
    if (grid.parentElement.querySelector('.skm-other-brands-link')) return;
    // Find the "Other" tile (button with data-brand="other"), fallback to
    // the last child if no semantic marker is present.
    var others = grid.querySelector('button[data-brand="other"]')
      || grid.children[grid.children.length - 1];
    if (!others) return;
    var fallbackUrl = others.getAttribute('data-brand-url') || '/pages/other-brands';

    var link = document.createElement('a');
    link.className = 'skm-other-brands-link';
    link.href = fallbackUrl; // for keyboard / fallback navigation
    link.innerHTML = 'Don’t see your brand? <span>Click here</span>';
    link.addEventListener('click', function (e) {
      // Trigger the original button's click handler so the page's drill-
      // down logic opens the panel inline.
      e.preventDefault();
      others.click();
      // Scroll into view so the user sees the just-opened panel
      setTimeout(function () {
        try {
          others.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (_) { /* old browsers */ }
      }, 80);
    });

    grid.parentNode.insertBefore(link, grid.nextSibling);
  }

  /* ===================================================================
     9) Watch the footer. When it scrolls into view, add a body class so
     CSS clips the Birdeye chat iframe to just the icon (footer links
     stay clickable). When the footer leaves the viewport again, remove
     the class so the full chat bubble + notification text re-appear.
     =================================================================== */
  function watchFooterForChatClip() {
    if (!isMobile()) return;
    var footer = document.querySelector('footer');
    if (!footer || typeof IntersectionObserver === 'undefined') return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          document.body.classList.add('skm-footer-visible');
        } else {
          document.body.classList.remove('skm-footer-visible');
        }
      });
    }, {
      // Trigger when the footer is within the bottom 250px of the viewport
      // (i.e. about to enter the area covered by the Birdeye bubble).
      rootMargin: '0px 0px -250px 0px',
      threshold: 0,
    });
    io.observe(footer);
  }

  /* ===================================================================
     11) Drill-panel model pill labels, strip the brand prefix from every
     model name so the pill reads cleanly:
       "Samsung Galaxy S25" → "Galaxy S25"
       "Samsung S25 Ultra"  → "Galaxy S25 Ultra"  (insert missing Galaxy)
       "Samsung Note 20"    → "Galaxy Note 20"
       "Google Pixel 9 Pro" → "Pixel 9 Pro"
       "Oppo Find X8 Pro"   → "Find X8 Pro"
       "Oppo Reno 12"       → "Reno 12"
       "Oppo A98"           → "A98"
     The drill-down JS injects fresh markup every time a brand/series is
     opened, so we use a MutationObserver on each drill-panel-inner to
     rewrite text on the fly.
     =================================================================== */
  function setupBrandLabelRewriter() {
    if (!isMobile()) return;
    var panelInners = document.querySelectorAll('.drill-panel-outer .drill-panel-inner');
    if (panelInners.length === 0) return;

    function rewriteText(raw) {
      var trimmed = raw.replace(/\s+/g, ' ').trim();
      if (!trimmed) return raw;
      // Samsung, strip "Samsung " prefix
      if (/^Samsung\s+Galaxy\b/i.test(trimmed)) {
        raw = raw.replace(/Samsung\s+Galaxy/i, 'Galaxy');
        trimmed = raw.replace(/\s+/g, ' ').trim();
      } else if (/^Samsung\s+/i.test(trimmed) && !/Galaxy/i.test(trimmed)) {
        raw = raw.replace(/Samsung\s+/i, 'Galaxy ');
        trimmed = raw.replace(/\s+/g, ' ').trim();
      }
      // For Samsung tablets, also strip "Galaxy " when it precedes "Tab"
      // ("Galaxy Tab S9" → "Tab S9").
      if (/^Galaxy\s+Tab\b/i.test(trimmed)) {
        return raw.replace(/Galaxy\s+Tab/i, 'Tab');
      }
      // iPad Pro models, strip the "iPad " prefix so they read just
      // "Pro 11 (2018)" / "Pro 12.9 (2024)" etc. The page is already
      // titled "iPad Pro Series" so the iPad prefix is redundant.
      if (/^iPad\s+Pro\b/i.test(trimmed)) {
        return raw.replace(/iPad\s+Pro/i, 'Pro');
      }
      // Google, drop "Google " prefix
      if (/^Google\s+/i.test(trimmed)) {
        return raw.replace(/Google\s+/i, '');
      }
      // Oppo, drop "Oppo " prefix
      if (/^Oppo\s+/i.test(trimmed)) {
        return raw.replace(/Oppo\s+/i, '');
      }
      return raw;
    }

    function rewrite(root) {
      root.querySelectorAll('a').forEach(function (a) {
        // Walk every descendant text node, NOT just direct children, some
        // brand pages nest the label inside a <span class="font-bold">.
        // Skip material-symbol icon spans so we don't try to rewrite
        // "chevron_right" / "smartphone" / etc.
        var walker = document.createTreeWalker(a, NodeFilter.SHOW_TEXT, {
          acceptNode: function (n) {
            var p = n.parentElement;
            if (!p) return NodeFilter.FILTER_REJECT;
            if (p.classList.contains('material-symbols-outlined')) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        var node;
        while ((node = walker.nextNode())) {
          var rewritten = rewriteText(node.nodeValue);
          if (rewritten !== node.nodeValue) {
            node.nodeValue = rewritten;
          }
        }
      });
    }

    panelInners.forEach(function (inner) {
      rewrite(inner);
      var obs = new MutationObserver(function () { rewrite(inner); });
      obs.observe(inner, { childList: true, subtree: true });
    });
  }

  /* ===================================================================
     12) Tag the body with the current page handle so per-page CSS can
     target specific pricing templates (e.g. iPhone 17 side-by-side
     mobile layout test). Mobile only, no impact on desktop.
     =================================================================== */
  function tagBodyWithPageHandle() {
    if (!isMobile()) return;
    var path = location.pathname || '';
    var m = path.match(/^\/pages\/([a-z0-9-]+)/i);
    if (!m) return;
    document.body.classList.add('skm-page-' + m[1]);
  }

  /* ===================================================================
     Smooth <details> dropdowns inside the burger menu.
     The native <summary> toggle snaps. We intercept, animate the
     content wrapper's max-height + opacity, and keep the chevron
     rotation in sync (CSS in mobile-optimisations.css handles the
     `.skk-closing` override).
     =================================================================== */
  function setupSmoothMenuDropdowns() {
    if (!isMobile()) return;
    var menu = document.getElementById('mobileMenu');
    if (!menu) return;
    var detailsList = menu.querySelectorAll('nav details');
    if (!detailsList.length) return;

    var EASE = 'max-height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease';

    detailsList.forEach(function (d) {
      if (d.dataset.smoothBound === '1') return;
      d.dataset.smoothBound = '1';

      var summary = d.querySelector(':scope > summary');
      var content = null;
      // Pick the first non-summary direct child as the animatable panel.
      for (var i = 0; i < d.children.length; i++) {
        if (d.children[i].tagName !== 'SUMMARY') { content = d.children[i]; break; }
      }
      if (!summary || !content) return;

      content.style.overflow = 'hidden';
      content.style.transition = EASE;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (d.classList.contains('skk-animating')) return;
        d.classList.add('skk-animating');

        if (!d.open) {
          // OPEN, set [open] so chevron rotates, then animate content in
          d.open = true;
          content.style.maxHeight = '0px';
          content.style.opacity = '0';
          // Force reflow so the browser registers the starting state
          void content.offsetHeight;
          var target = content.scrollHeight;
          requestAnimationFrame(function () {
            content.style.maxHeight = target + 'px';
            content.style.opacity = '1';
          });
          setTimeout(function () {
            // Clear inline height so future content changes can re-flow
            if (d.open) content.style.maxHeight = '';
            d.classList.remove('skk-animating');
          }, 300);
        } else {
          // CLOSE, flip a CSS-only class so the chevron rotates back
          // immediately while we animate the content collapsing
          d.classList.add('skk-closing');
          content.style.maxHeight = content.scrollHeight + 'px';
          void content.offsetHeight;
          requestAnimationFrame(function () {
            content.style.maxHeight = '0px';
            content.style.opacity = '0';
          });
          setTimeout(function () {
            d.open = false;
            d.classList.remove('skk-closing');
            d.classList.remove('skk-animating');
          }, 280);
        }
      });
    });
  }

  /* ===================================================================
     13) Brand-section filter drawer delegation.
     The Apple/Samsung/Google/Oppo brand sections render a trigger
     button + bottom-sheet aside + apply/close controls. The per-section
     IIFE that wires the buttons depends on executeInjectedScripts()
     re-creating the <script> tags AFTER innerHTML injection. On some
     mobile browsers (or under flaky network conditions) that hand-off
     can break, leaving the brand drawer dead. Using document-level
     event delegation here makes the open/close behaviour resilient to
     when (or whether) the per-section script ran.
     =================================================================== */
  // build: brand-filters-delegate v2 (force push 2026-05-27)
  function setupBrandFiltersDelegate() {
    if (window.__skmBrandFiltersBound) return;
    window.__skmBrandFiltersBound = true;

    function brandFromId(id, suffix) {
      var re = new RegExp('^Mobile(.+?)Filters' + suffix + '$');
      var m = id && id.match(re);
      return m ? m[1].toLowerCase() : null;
    }

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var openEl = t.closest('[id^="Mobile"][id$="FiltersToggle"]');
      if (openEl) {
        var bOpen = brandFromId(openEl.id, 'Toggle');
        if (bOpen) document.body.classList.add('skm-' + bOpen + '-filters-open');
        return;
      }
      var closeEl = t.closest('[id^="Mobile"][id$="FiltersClose"]');
      if (closeEl) {
        var bClose = brandFromId(closeEl.id, 'Close');
        if (bClose) document.body.classList.remove('skm-' + bClose + '-filters-open');
        return;
      }
      var applyEl = t.closest('[id^="Mobile"][id$="FiltersApply"]');
      if (applyEl) {
        var bApply = brandFromId(applyEl.id, 'Apply');
        if (bApply) document.body.classList.remove('skm-' + bApply + '-filters-open');
        return;
      }
      var bdEl = t.closest('[id^="Mobile"][id$="FiltersBackdrop"]');
      if (bdEl) {
        var bBd = brandFromId(bdEl.id, 'Backdrop');
        if (bBd) document.body.classList.remove('skm-' + bBd + '-filters-open');
        return;
      }
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var cls = (document.body.className || '').split(/\s+/);
      cls.forEach(function (c) {
        if (/^skm-.+-filters-open$/.test(c)) document.body.classList.remove(c);
      });
    });
  }

  /* ===================================================================
     14) Brand-section CARD filter delegation.
     Each brand section (Apple/Samsung/Google/Oppo) ships a per-section
     IIFE that wires change/click listeners on its SERIES checkboxes and
     SIZE pills to filter `.{Brand}-card` items. On Apple this fires
     reliably because the section is the FIRST one injected into the
     drill-panel and its <script> tag executes cleanly. On the other
     brands the executeInjectedScripts re-creation occasionally fails
     to bind on real mobile browsers, meaning the chips look pressed
     but nothing filters.

     This delegation listens at the document level, recomputes the
     active set from the actual DOM (which checkboxes are checked, which
     size pill has .active), and updates the cards + count regardless
     of whether the per-section IIFE bound or not. Both handlers can
     coexist: the per-section one also calls applyFilters() locally;
     ours runs again afterward and yields the same result because both
     read the same DOM state.
     =================================================================== */
  function setupBrandCardFilters() {
    if (window.__skmBrandCardFiltersBound) return;
    window.__skmBrandCardFiltersBound = true;

    // Liquid uses different casing for class names vs IDs (e.g.
    // `IPhone-card` class but `IphoneResultCount` id). Map them.
    var BRANDS = [
      { key: 'samsung', cardCls: 'Samsung-card', sizeCls: 'Samsung-size-filter', cbCls: 'Samsung-series-checkbox', idPrefix: 'Samsung', mobIdPrefix: 'Samsung' },
      { key: 'google',  cardCls: 'Google-card',  sizeCls: 'Google-size-filter',  cbCls: 'Google-series-checkbox',  idPrefix: 'Google',  mobIdPrefix: 'Google' },
      { key: 'oppo',    cardCls: 'Oppo-card',    sizeCls: 'Oppo-size-filter',    cbCls: 'Oppo-series-checkbox',    idPrefix: 'Oppo',    mobIdPrefix: 'Oppo' },
      // iPhone uses `iphone-card` (lowercase) for cards and unprefixed
      // `series-checkbox` / `size-filter` for filter controls (its IIFE
      // was the original; the brand-prefixed pattern came later for the
      // other brands).
      { key: 'iphone',  cardCls: 'iphone-card',  sizeCls: 'size-filter',  cbCls: 'series-checkbox',  idPrefix: 'Iphone',  mobIdPrefix: 'Iphone' }
    ];

    function findBrand(el) {
      if (!el || !el.classList) return null;
      for (var i = 0; i < BRANDS.length; i++) {
        var b = BRANDS[i];
        if (el.classList.contains(b.cbCls) || el.classList.contains(b.sizeCls) || el.classList.contains(b.cardCls)) return b;
      }
      return null;
    }

    function applyForBrand(b) {
      var allCb = document.querySelectorAll('.' + b.cbCls);
      var checked = [];
      // Sync a `.skm-chip-checked` class onto each checkbox's wrapping
      // label so the visual state doesn't depend on `:has(input:checked)`
      //, that selector silently no-ops on older iOS Safari (< 15.4) and
      // some embedded webviews, leaving chips that filter the cards but
      // never look pressed.
      allCb.forEach(function (cb) {
        var lbl = cb.closest('label');
        if (cb.checked) {
          checked.push(cb.value);
          if (lbl) lbl.classList.add('skm-chip-checked');
        } else if (lbl) {
          lbl.classList.remove('skm-chip-checked');
        }
      });
      var activeSizeBtn = document.querySelector('.' + b.sizeCls + '.active');
      var activeSize = activeSizeBtn ? activeSizeBtn.dataset.size : null;
      var cards = document.querySelectorAll('.' + b.cardCls);
      var visible = 0;
      cards.forEach(function (card) {
        var matchSeries = checked.length === 0 || checked.indexOf(card.dataset.series) !== -1;
        var matchSize = !activeSize || card.dataset.size === activeSize;
        var show = matchSeries && matchSize;
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      var ce = document.getElementById(b.idPrefix + 'ResultCount');
      if (ce) ce.textContent = 'Showing ' + visible + ' model' + (visible === 1 ? '' : 's');
      var ac = document.getElementById('Mobile' + b.mobIdPrefix + 'FiltersCount');
      if (ac) ac.textContent = visible + ' model' + (visible === 1 ? '' : 's');
      var badge = document.getElementById('Mobile' + b.mobIdPrefix + 'FiltersBadge');
      if (badge) {
        var n = checked.length + (activeSize ? 1 : 0);
        if (n > 0) { badge.textContent = String(n); badge.classList.remove('hidden'); }
        else { badge.classList.add('hidden'); }
      }
    }

    document.addEventListener('change', function (e) {
      var b = findBrand(e.target);
      if (b) applyForBrand(b);
    }, true);

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      // SIZE pill, visual toggle (.active) then filter.
      var sizeBtn = null;
      var matchedBrand = null;
      for (var i = 0; i < BRANDS.length; i++) {
        var b = BRANDS[i];
        sizeBtn = e.target.closest('.' + b.sizeCls);
        if (sizeBtn) { matchedBrand = b; break; }
      }
      if (sizeBtn && matchedBrand) {
        var wasActive = sizeBtn.classList.contains('active');
        // Reset all size pills for this brand to non-active visual state.
        document.querySelectorAll('.' + matchedBrand.sizeCls).forEach(function (btn) {
          btn.classList.remove('border-primary', 'bg-primary/10', 'text-primary', 'active');
          btn.classList.add('border-slate-200', 'dark:border-slate-700');
        });
        if (!wasActive) {
          sizeBtn.classList.add('border-primary', 'bg-primary/10', 'text-primary', 'active');
          sizeBtn.classList.remove('border-slate-200', 'dark:border-slate-700');
        }
        applyForBrand(matchedBrand);
        return;
      }
      // CLEAR button, reset all series + size for the brand.
      var clearBtn = e.target.closest('button[id^="Clear"][id$="Filters"]');
      if (clearBtn) {
        var m = clearBtn.id.match(/^Clear(.+?)Filters$/);
        if (!m) return;
        var brandIdPart = m[1];
        var found = null;
        for (var j = 0; j < BRANDS.length; j++) {
          if (BRANDS[j].idPrefix === brandIdPart || BRANDS[j].mobIdPrefix === brandIdPart) { found = BRANDS[j]; break; }
        }
        if (!found) return;
        document.querySelectorAll('.' + found.cbCls).forEach(function (cb) { cb.checked = false; });
        document.querySelectorAll('.' + found.sizeCls).forEach(function (btn) {
          btn.classList.remove('border-primary', 'bg-primary/10', 'text-primary', 'active');
          btn.classList.add('border-slate-200', 'dark:border-slate-700');
        });
        applyForBrand(found);
      }
    }, true);

    // When the user taps the label that wraps a hidden checkbox the
    // browser is supposed to forward the click to the input and fire
    // `change`. On real iOS Safari, when the input has `display: none`
    // applied via CSS, the forwarding sometimes fires `click` but not
    // `change`, and our `change` listener above misses the event,
    // leaving the chips dead even though they look pressed.
    //
    // The fix: take ownership of the label click ourselves. Prevent the
    // default native forwarding, flip the checkbox state by hand, dispatch
    // a synthetic `change` event for any per-section IIFE that's also
    // listening, then re-apply our own filter. This works the same in
    // every browser, with or without `display: none` on the input.
    document.addEventListener('click', function (e) {
      var label = e.target && e.target.closest ? e.target.closest('label') : null;
      if (!label) return;
      var cb = label.querySelector('input[type="checkbox"]');
      if (!cb) return;
      var brand = findBrand(cb);
      if (!brand) return;
      // If the actual click target IS the input (e.g. on desktop where
      // the checkbox isn't display:none and the user clicked it directly),
      // let the native toggle happen and just re-apply.
      if (e.target === cb) {
        applyForBrand(brand);
        return;
      }
      // Otherwise we own the toggle.
      e.preventDefault();
      cb.checked = !cb.checked;
      try {
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (_) {
        // Older browsers: fallback to legacy Event constructor.
        var ev = document.createEvent('HTMLEvents');
        ev.initEvent('change', true, false);
        cb.dispatchEvent(ev);
      }
      applyForBrand(brand);
    }, true);
  }

  /* ===================================================================
     15) Smooth page-transition into Order-In product pages.
     The user reported that going BACK from a product page feels smooth
     (the drill-panel re-renders + browser back uses the bfcache) but
     clicking INTO a product card is an abrupt jump. The drill-panel
     slides; the navigation should glide too.

     Fade the body to opacity 0 right before navigating to any URL that
     starts with /pages/order-in- (the Order-In product URLs). Browser
     handles the rest, by the time the new page paints, our boot CSS
     resets opacity to 1 (no class on body), so the new page fades in
     too.
     =================================================================== */
  function setupOrderInTransition() {
    if (window.__skmOrderInTransitionBound) return;
    window.__skmOrderInTransitionBound = true;

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return; // ignore middle/right clicks
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // new tab
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var href = a.getAttribute('href') || '';
      // Only fire for in-store Order-In product URLs.
      if (!/^\/pages\/order-in-/.test(href)) return;
      // Same-origin only (the URL is a relative path so it's already same-origin,
      // but be defensive against `target=_blank` or `rel=external`).
      if (a.target && a.target !== '' && a.target !== '_self') return;
      if (/(^|\s)external(\s|$)/i.test(a.rel || '')) return;

      e.preventDefault();
      document.body.classList.add('skm-page-fading');
      // Match the CSS transition duration. Add a tiny safety buffer so the
      // browser commits the opacity change before navigation kicks in.
      setTimeout(function () { window.location.assign(href); }, 230);
    });

    // Restore opacity when returning via bfcache so the prior page doesn't
    // appear blank for a flash.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) document.body.classList.remove('skm-page-fading');
    });
  }

  /* ===================================================================
     Boot
     =================================================================== */
  function init() {
    injectMobileTelCta();
    injectStickyCta();
    setupFilterDrawer();
    restructureReviewsCarousel();
    reorderReviewsHeader();
    trimStoreParagraph();
    watchFooterForChatClip();
    injectOtherBrandsLink();
    setupBrandLabelRewriter();
    tagBodyWithPageHandle();
    setupSmoothMenuDropdowns();
    setupBrandFiltersDelegate();
    setupBrandCardFilters();
    setupOrderInTransition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // If the viewport crosses the breakpoint (rotate / resize), re-evaluate.
  // We do NOT remove already-injected elements, the CSS hides them via
  // `@media (min-width: 768px)` (implicitly: rules only fire under 767px).
  // Instead, on a transition into mobile we ensure injection has run.
  if (MOBILE_MQ.addEventListener) {
    MOBILE_MQ.addEventListener('change', function () {
      if (MOBILE_MQ.matches) init();
    });
  } else if (MOBILE_MQ.addListener) {
    // Safari < 14 fallback
    MOBILE_MQ.addListener(function () {
      if (MOBILE_MQ.matches) init();
    });
  }
})();
